import { Slide } from '@features/powerpoint';
import { DEFAULT_SLIDE_MASTER } from '../../../slides/slide-master';
import { formatters } from '@utils/formattersV2';
import { Lci3SalesMetrics } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import {
  Subdimension,
  SubdimensionsByName,
} from '@features/reports/subdomains/visitation';
import {
  OVERALL_LINE_ITEM_OFFSET,
  OverallLineItemTextsOptions,
  texts,
} from '../../../slides/sales-dimensions/texts';
import { shapes } from '../../../slides/sales-dimensions/shapes';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import { FormSchema } from '@features/executive-report/subdomains/visitation';
import { images } from '../../../slides/sales-dimensions/images';
import { visibleSlide } from '@features/executive-report/slides';
import { DimensionRecommendationsSchema } from '@features/executive-report/subdomains/visitation';
import { headerTabs, TabId } from './header-tabs';
import { SalesDimensionsSlideBaseOptions } from '@features/executive-report/slides/sales-dimensions/content';
import { theme } from '@features/executive-report/domain/theme';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';

type TabIdOptions = {
  activeHeaderTab: TabId;
};

type SalesDimensionsSlideOptions = SalesDimensionsSlideBaseOptions &
  TabIdOptions;

const overallLineItemTexts = (
  options: OverallLineItemTextsOptions
): NonNullable<Slide['texts']> => [
  {
    text: options.label.toUpperCase(),
    options: {
      fontSize: 9,
      color: theme.colors.white,
      h: 0.25,
      w: 2.71,
      x: 0.94,
      y: 3.54 + OVERALL_LINE_ITEM_OFFSET * options.index,
    },
  },
  {
    text: options.formattedValue,
    options: {
      fontSize: 20,
      color: theme.colors.white,
      bold: true,
      h: 0.44,
      w: 1.43,
      x: 0.94,
      y: 3.72 + OVERALL_LINE_ITEM_OFFSET * options.index,
    },
  },
  {
    text: options.liftFormatter
      ? formatWithSign(options.lift, options.liftFormatter)
      : '',
    options: {
      fontSize: 20,
      color: theme.colors.inmarketGreen,
      h: 0.44,
      w: 1.45,
      x: 2.36,
      y: 3.72 + OVERALL_LINE_ITEM_OFFSET * options.index,
    },
  },
];

const salesDimensionsSlide = (
  options: SalesDimensionsSlideOptions
): Slide[] => {
  return visibleSlide({
    slide: {
      slideMaster: DEFAULT_SLIDE_MASTER,
      texts: texts(
        options,
        headerTabs(options.activeHeaderTab),
        overallLineItemTexts
      ),
      shapes: shapes(options.subdimensions),
      images: images(options.subdimensions),
    },
    visible: options.visible,
  });
};

const subdimensionToSalesDimensionOptions =
  (subdimensionImages: FormSchema['subdimensionImagesByNameAndValue']) =>
  (
    subdimension: Subdimension
  ): SalesDimensionsSlideOptions['subdimensions'][number] => {
    return {
      name: subdimension.value,
      incrementalSalesLift:
        subdimension.salesMetrics?.incrementalSalesLift.total ?? null,
      incrementalSales:
        subdimension.salesMetrics?.incrementalSales.total ?? null,
      impressions: subdimension.visitMetrics.impressions,
      image:
        subdimensionImages[subdimension.name]?.[subdimension.value] ?? null,
    };
  };

const liftPartFormatter: OverallLineItemTextsOptions['liftFormatter'] = value =>
  formatters.percentage(value, { decimals: 1 });

type SalesDimensionsSlidesOptions = {
  salesMetrics: Lci3SalesMetrics;
  subdimensionsByName: SubdimensionsByName;
  recommendations: DimensionRecommendationsSchema;
  includedSlides: FormSchema['includedSlides']['sales'];
  subdimensionImages: FormSchema['subdimensionImagesByNameAndValue'];
};

export const salesDimensionsSlides = (
  options: SalesDimensionsSlidesOptions
): Slide[] => {
  const overallMetricsSales: SalesDimensionsSlideOptions['overallMetrics'] = [
    {
      label: 'Incremental Sales and Lift',
      formattedValue: formatters.currency(
        options.salesMetrics.incrementalSales.total,
        { decimals: 1 }
      ),
      lift: options.salesMetrics.incrementalSalesLift.total ?? 0,
      liftFormatter: formatters.lift,
    },
    {
      label: 'Transaction Size and Lift',
      formattedValue: formatters.currency(
        options.salesMetrics.exposedTransactionSize.total,
        { decimals: 0 }
      ),
      lift: options.salesMetrics.transactionSizeLift.total ?? 0,
      liftFormatter: liftPartFormatter,
    },
    {
      label: 'Penetration and Lift',
      formattedValue: formatters.percentage(
        options.salesMetrics.exposedPenetration.total,
        { decimals: 1 }
      ),
      lift: options.salesMetrics.penetrationLift.total ?? 0,
      liftFormatter: liftPartFormatter,
    },
    {
      label: 'Transaction Rate and Lift',
      formattedValue: formatters.number(
        options.salesMetrics.exposedTransactionRate.total,
        { decimals: 1 }
      ),
      lift: options.salesMetrics.transactionRateLift.total ?? 0,
      liftFormatter: liftPartFormatter,
    },
  ];

  const salesDimensionsSlideOptions: SalesDimensionsSlideOptions[] = [
    ...(options.subdimensionsByName.aud
      ? ([
          {
            dimensionName: apiSlugToDimensionName.aud,
            activeHeaderTab: 'who',
            subdimensions: options.subdimensionsByName.aud.map(
              subdimensionToSalesDimensionOptions(options.subdimensionImages)
            ),
            recommendation: options.recommendations.who,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHO: </lci><b>Audiences</b>',
            visible: options.includedSlides.who,
          },
        ] as const)
      : []),
    ...(options.subdimensionsByName.creative
      ? ([
          {
            dimensionName: apiSlugToDimensionName.creative,
            activeHeaderTab: 'what',
            subdimensions: options.subdimensionsByName.creative.map(
              subdimensionToSalesDimensionOptions(options.subdimensionImages)
            ),
            recommendation: options.recommendations.what,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHAT: </lci><b>Creatives</b>',
            visible: options.includedSlides.what,
          },
        ] as const)
      : []),
    ...(options.subdimensionsByName.channel
      ? ([
          {
            dimensionName: apiSlugToDimensionName.channel,
            activeHeaderTab: 'where',
            subdimensions: options.subdimensionsByName.channel.map(
              subdimensionToSalesDimensionOptions(options.subdimensionImages)
            ),
            recommendation: options.recommendations.where,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHERE: </lci><b>Channels</b>',
            visible: options.includedSlides.where,
          },
        ] as const)
      : []),
  ];

  return salesDimensionsSlideOptions.flatMap(options =>
    salesDimensionsSlide(options)
  );
};
